import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { requestRegister } from '../../../api/registration/registration.actions';

import RoleSelection from '../../../components/screens/auth/registration/role-selection/RoleSelection';
import PermissionSelection from '../../../components/screens/auth/registration/permission-selection/PermissionSelection';
import PersonalInformation from '../../../components/screens/auth/registration/personal-information/PersonalInformation';
import RegistrationSuccess from '../../../components/screens/auth/registration/registration-success/RegistrationSuccess';

const RegistrationContainer = () => {
  const { organisation } = useSelector(({ party }) => party);

  const [roles, setRoles] = useState([]);
  const [screenStep, setScreenStep] = useState(1);
  const [permissions, setPermissions] = useState([]);
  const [personalInformation, setPersonalInformation] = useState({});
  const [orgForScreen, setOrgForScreen] = useState(undefined);

  const navigate = useNavigate();

  const { personExternalReference } = useParams();

  const [searchParams] = useSearchParams();

  const dispatch = useDispatch();

  useEffect(() => {
    if (organisation) {
      setOrgForScreen(organisation);
    }
  }, [organisation]);

  const resetPersonalInformation = () => {
    const personalInfo = {
      title: personalInformation.title ? personalInformation.title : '',
      jobTitle: personalInformation.jobTitle ? personalInformation.jobTitle : '',
      firstName: personalInformation.firstName ? personalInformation.firstName : '',
      lastName: personalInformation.lastName ? personalInformation.lastName : '',
      mobile: personalInformation.mobile ? personalInformation.mobile : '',
    };
    setPersonalInformation(personalInfo);
  };

  const addRole = (newRole) => {
    setRoles([...roles, newRole]);
  };

  const removeRole = (roleToRemove) => {
    setRoles(roles.filter((role) => role !== roleToRemove));
  };

  const isRoleInList = (role) => {
    return roles.includes(role);
  };

  const isRolesEmpty = () => {
    return roles.length === 0;
  };

  const addPermission = (newPermission) => {
    setPermissions([...permissions, newPermission]);
  };

  const removePermission = (permissionToRemove) => {
    setPermissions(permissions.filter((permission) => permission !== permissionToRemove));
  };

  const isPermissionInList = (permission) => {
    return permissions.includes(permission);
  };

  const register = (updatedPersonalInformation) => {
    const personalInformationVal = updatedPersonalInformation ? updatedPersonalInformation : personalInformation;
    const keyExternalReference = searchParams.get('keyExternalReference');

    const registration = {
      interests: roles,
      organisationExternalReference: keyExternalReference
        ? keyExternalReference
        : personalInformationVal.organisationExternalReference,
      notificationPermission: isPermissionInList('Notification'),
      contactPermission: isPermissionInList('Contact'),
      marketingPermission: isPermissionInList('Marketing'),
      companyName: personalInformationVal.companyName,
      tradingName: personalInformationVal.tradingName,
      incorporationDate: personalInformationVal.incorporationDate,
      webURL: personalInformationVal.webURL,
      taxRegistrationNumber: personalInformationVal.taxRegistrationNumber,
      companyRegistrationNumber: personalInformationVal.companyRegistrationNumber,
      importerCode: personalInformationVal.importerCode,
      addressLine1: personalInformationVal.addressLine1,
      addressLine2: personalInformationVal.addressLine2,
      city: personalInformationVal.city,
      province: personalInformationVal.province,
      country: personalInformationVal.country,
      postalCode: personalInformationVal.postalCode,
      title: personalInformationVal.title,
      jobTitle: personalInformationVal.jobTitle,
      firstName: personalInformationVal.firstName,
      lastName: personalInformationVal.lastName,
      mobile: personalInformationVal.mobile,
      broker: isRoleInList('Broker'),
      linkedRegistration: !!keyExternalReference,
      personExternalReference,
    };

    dispatch(
      requestRegister({
        registration,
        onSuccess: () => setScreenStep(4),
      })
    );
  };

  const determineScreenToDisplay = () => {
    if (screenStep && screenStep === 1) {
      return (
        <PermissionSelection
          setScreenStep={setScreenStep}
          addPermission={addPermission}
          removePermission={removePermission}
          isPermissionInList={isPermissionInList}
        />
      );
    }

    if (screenStep && screenStep === 2) {
      return (
        <PersonalInformation
          setScreenStep={setScreenStep}
          setPersonalInformation={setPersonalInformation}
          organisation={orgForScreen}
          register={register}
          setOrgForScreen={setOrgForScreen}
          personalInformation={personalInformation}
          resetPersonalInformation={resetPersonalInformation}
          linkedRegistration={!!searchParams.get('keyExternalReference')}
        />
      );
    }

    if (screenStep && screenStep === 3) {
      return (
        <RoleSelection
          setScreenStep={setScreenStep}
          addRole={addRole}
          removeRole={removeRole}
          isRoleInList={isRoleInList}
          isRolesEmpty={isRolesEmpty}
          register={register}
        />
      );
    }

    if (screenStep && screenStep === 4) {
      return <RegistrationSuccess navigateToLogin={() => navigate('/')} />;
    }
  };

  return determineScreenToDisplay();
};

export default RegistrationContainer;
